import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Segmented,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { IconRefresh, IconCertificate } from '@tabler/icons-react';

import { listCases } from '@/api/cases';
import { getCompliance } from '@/api/monitoring';
import type { IndicatorEvalNode } from '@/api/monitoring';
import { LoadingSkeleton } from '@/components/states/LoadingSkeleton';
import { ErrorState } from '@/components/states/ErrorState';
import { EmptyState } from '@/components/states/EmptyState';
import { IndicatorList } from './components/IndicatorList';

/**
 * 孵化器达标看板 — Sprint 20-extra 合并版。
 *
 * 合并源:
 *  - 原 /compliance  CompliancePage    达标评估 (GET /periods/:id/assessment)
 *  - 原 /readiness   ReadinessPage     就绪检查 (GET /periods/:id/readiness)
 *
 * Sprint23-Compliance:用户反馈"不要显示孵化器档案,要显示申请认证孵化器的级别"
 * 新两段式布局:
 *  ① Hero  — 申请认证级别(政策包 level 突出)+ 周期选择 + 重算
 *  ② 中部  — 21 指标 Table + 红绿灯 + "去录入"
 *  ③ 底部  — 差距摘要 + 材料缺口分组
 *
 * 数据流:
 *   GET /incubator-profile                            档案 hero
 *   GET /periods/{period_id}/assessment               主指标 + 总评(老 ρ-2 接口)
 *   GET /periods/{period_id}/readiness                材料就绪 + 缺口(老 Sprint7a 接口)
 *
 * TODO(Sprint 20-extra BE): 当前仍走两个 endpoint(assessment + readiness),都按 period_id 拉。
 * 理想:平台级 GET /threshold-check?policy_pack_code=SFL-INC-STD-2026 一把抓 21 指标 + 材料状态。
 * 主 agent 范围 — 子 agent 不新建 BE endpoint;此 TODO 留主 agent Sprint 21 评估。
 */

// Sprint 27:老 period_id 路径删后 PERIOD_SOURCE_NOTE 不再用 — Sprint 28 可整理

// ---------------------------------------------------------------------------
// 工具
// ---------------------------------------------------------------------------

// Sprint 35:删 evidenceStatusColor/Label/groupEvidence — 仅老 period_id 路径用,该路径已删

/** Sprint 35:递归收集 pending 指标(数据未录)+ 取所有叶子节点的 fail */
function collectByStatus(
  nodes: IndicatorEvalNode[],
  status: 'pending' | 'fail',
): IndicatorEvalNode[] {
  const out: IndicatorEvalNode[] = [];
  for (const n of nodes) {
    if (n.children && n.children.length > 0) {
      out.push(...collectByStatus(n.children, status));
    } else if (n.severity === status) {
      out.push(n);
    }
  }
  return out;
}

/** 指标代码 → 录入页路由(Sprint 35:简化 + 修死链)。
 * - T1-T4:孵化器档案字段(可跨案复用)→ /incubator/profile
 * - 其他 T:案工作台「指标绑定」Tab(录 manual_value,Sprint 22 本意 — 只对本案有效)
 *   原 hardcoded T5+→/subjects?indicator=... 是死链:SubjectsPage 不响应该 query。
 */
function inputRouteForIndicator(code: string, activeCaseId?: string): string {
  if (/^T0?[1-4]$/.test(code)) {
    return `/incubator/profile?return_to=${encodeURIComponent('/compliance')}`;
  }
  // 案工作台「指标绑定」Tab(用 hash 让目标页 focus)
  if (activeCaseId) {
    return `/cases/${activeCaseId}#tab=indicators&focus=${encodeURIComponent(code)}`;
  }
  // 兜底:无 active case 时跳主体管理(理论上调本函数前已守 active case 存在)
  return '/subjects';
}

// ---------------------------------------------------------------------------
// 级别 → 中文标签
// ---------------------------------------------------------------------------

const LEVEL_CN: Record<string, string> = {
  national: '国家级',
  provincial: '省级',
  municipal: '市级',
  district: '区级',
  standard: '标准级',
  excellent: '卓越级',
  benchmark: '标杆级',
};

function levelLabel(level: string | undefined | null): string {
  if (!level) return '—';
  return LEVEL_CN[level.toLowerCase()] ?? level;
}

// ---------------------------------------------------------------------------
// 主页
// ---------------------------------------------------------------------------

/**
 * 达标看板页 (v4 M3-ρ-2 / Sprint 20-extra 合并版)。
 *
 * 不变量:圣丰联 2025 黄金回归 — 场地 8554.21㎡ / 专职 87.5% / 直投 9.38%(未达 A)/ 外融 25%(OR 通过 B)。
 */
export function CompliancePage() {
  const navigate = useNavigate();

  // Sprint23:申请认证资质名(从最新 active 案 → policy_pack_name 取);fallback 通用标题
  // 拉本租户所有案,客户端筛 active 状态(BE list status 单值 query 不便)
  const casesQuery = useQuery({
    queryKey: ['compliance-active-cases'],
    queryFn: () => listCases({ page_size: 50 }),
    staleTime: 60_000,
  });
  // Sprint57:支持多 active case 切换(同主体同时申报多级别孵化器,如标准+标杆)。
  // activeCases 是全部 active(按申报年度+created_at 倒序),selectedCaseId 决定看板视角。
  const activeCases = useMemo(() => {
    const items = casesQuery.data?.items ?? [];
    // Sprint 35:加 frozen — 冻结案是申报临门一脚,必须显示在看板
    const active = items.filter((c) =>
      ['draft', 'frozen', 'submitted', 'reviewing'].includes(String(c.status)),
    );
    // 取申报年度最新;同年取 created_at 最新
    active.sort((a, b) => {
      const ya = a.declare_year ?? 0;
      const yb = b.declare_year ?? 0;
      if (yb !== ya) return yb - ya;
      return String(b.created_at ?? '').localeCompare(String(a.created_at ?? ''));
    });
    return active;
  }, [casesQuery.data]);

  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  // 默认/校正:列表变化时,若当前选中已不在 active 列表,回退到第一个
  useEffect(() => {
    if (activeCases.length === 0) {
      if (selectedCaseId !== null) setSelectedCaseId(null);
      return;
    }
    if (!selectedCaseId || !activeCases.find((c) => c.id === selectedCaseId)) {
      setSelectedCaseId(activeCases[0].id);
    }
  }, [activeCases, selectedCaseId]);

  const activeCase = useMemo(
    () =>
      activeCases.find((c) => c.id === selectedCaseId) ?? activeCases[0] ?? null,
    [activeCases, selectedCaseId],
  );

  const packName = activeCase?.policy_pack_name;
  const qualName = activeCase?.declared_qualification_name ?? activeCase?.qualification_name;

  // Sprint23-hotfix2:无活跃申报案时,看板下半部全部隐藏,只显 Hero + 友好引导
  const noActiveCase = !casesQuery.isLoading && !casesQuery.isError && !activeCase;

  const handleRefresh = () => {
    if (activeCase) monitoringQuery.refetch();
  };

  // Sprint 27:case-driven 指标 — 用 active case 的 subject_id + policy_pack_id 调
  // GET /monitoring/compliance,直接拉案级 19 项实时算结果,跳过 period_id 路径。
  // period_id 路径(getAssessment)保留作 fallback,但用户场景已不再依赖。
  const monitoringQuery = useQuery({
    // Sprint27-hotfix7:加 case_id 进 queryKey + 透传 — 让 BE 叠加案级 manual_value
    queryKey: ['monitoring-compliance', activeCase?.id, activeCase?.subject_id, activeCase?.policy_pack_id],
    queryFn: () =>
      getCompliance({
        subject_id: activeCase!.subject_id,
        policy_pack_id: activeCase!.policy_pack_id,
        case_id: activeCase!.id,
      }),
    enabled: !!activeCase?.subject_id && !!activeCase?.policy_pack_id,
    staleTime: 30_000,
  });
  const monitoringPayload = monitoringQuery.data?.payload;

  return (
    <Space direction="vertical" size={14} style={{ width: '100%' }}>
      {/* Sprint57:多 active case 切换(同主体同时申报多级别,如标准+标杆) */}
      {activeCases.length > 1 && (
        <Card styles={{ body: { padding: '10px 16px' } }}>
          <Space align="center" wrap>
            <Typography.Text strong>切换申报案:</Typography.Text>
            <Segmented
              value={selectedCaseId ?? activeCases[0].id}
              onChange={(v) => setSelectedCaseId(String(v))}
              options={activeCases.map((c) => ({
                label: (
                  <span style={{ padding: '0 4px' }}>
                    {c.declared_qualification_name ?? c.qualification_name ?? '未命名资质'}
                    {c.declare_year ? (
                      <Typography.Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
                        {c.declare_year}
                      </Typography.Text>
                    ) : null}
                  </span>
                ),
                value: c.id,
              }))}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              共 {activeCases.length} 个进行中
            </Typography.Text>
          </Space>
        </Card>
      )}

      {/* ────────────────────────── ① HERO — 申请认证资质 ────────────────────────── */}
      <Card
        styles={{ body: { padding: 20 } }}
        style={{
          background:
            'linear-gradient(135deg, var(--color-brand-cyan, #0E7C66) 0%, var(--color-brand-navy, #0a2540) 100%)',
          color: '#fff',
          border: 'none',
        }}
      >
        <Space size={16} align="center" style={{ width: '100%' }} wrap>
          <IconCertificate size={44} style={{ color: '#fff', flex: 'none' }} />
          <div style={{ flex: 1, minWidth: 280 }}>
            <div
              style={{
                fontSize: 12,
                letterSpacing: 2,
                color: 'rgba(255,255,255,0.7)',
                marginBottom: 4,
              }}
            >
              正在申请认证
            </div>
            <Typography.Title
              level={3}
              style={{ margin: 0, color: '#fff', fontWeight: 600 }}
            >
              {qualName ?? '孵化器达标看板'}
            </Typography.Title>
            <Space size={8} style={{ marginTop: 8 }} wrap>
              {packName && (
                <Tag color="cyan" style={{ fontSize: 13, padding: '2px 10px' }}>
                  依据:{packName}
                </Tag>
              )}
              {activeCase?.declare_year && (
                <Tag
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    border: 'none',
                  }}
                >
                  {activeCase.declare_year} 年度
                </Tag>
              )}
              {activeCase && (
                <Tag color={['frozen', 'submitted', 'reviewing'].includes(String(activeCase.status)) ? 'blue' : 'gold'}>
                  {activeCase.status === 'draft' ? '草稿 · 实时' : '已冻结 · 权威'}
                </Tag>
              )}
              {!activeCase && !casesQuery.isLoading && (
                <Typography.Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
                  本租户暂无活跃申报案 · 去「申报案例」创建
                </Typography.Text>
              )}
            </Space>
            {/* Sprint 50:政策依据小字(对齐 xlsx「指标达标看板」备注列,鼓励用户去政策包查原文) */}
            {packName && activeCase?.policy_pack_id && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                依据政策:
                <a
                  onClick={() => navigate(`/policy-packs?selected=${activeCase.policy_pack_id}`)}
                  style={{
                    color: 'rgba(255,255,255,0.85)',
                    marginLeft: 4,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  {packName}
                </a>
                <span style={{ marginLeft: 6 }}>(点击查看政策包原文)</span>
              </div>
            )}
          </div>
        </Space>
      </Card>

      {/* ────────────────────────── 无活跃申报案 — 友好空态 ────────────────────────── */}
      {noActiveCase && (
        <Card styles={{ body: { padding: 24 } }}>
          <EmptyState
            title="目前无认证申请"
            description="本看板的指标达标情况随「申报案例」联动展示。创建一个申报案,选择目标资质(如标准级孵化器),系统将自动汇集 21 项指标的实时达标状态、差距清单、材料缺口。"
            action={
              <Space>
                <Button type="primary" onClick={() => navigate('/cases')}>
                  去申报案例创建
                </Button>
                <Button onClick={() => navigate('/policy-packs')}>
                  先看政策包配置
                </Button>
              </Space>
            }
          />
        </Card>
      )}

      {/* ────────────────────────── Sprint 27:case-driven monitoring ────────────────────────── */}
      {/* 有 active case 时直接用案级指标实时算结果,不需 period_id 选择 */}
      {!noActiveCase && activeCase && (
        <Card styles={{ body: { padding: 12 } }}>
          <Space
            style={{ width: '100%', justifyContent: 'space-between' }}
            align="center"
            wrap
          >
            <Typography.Text type="secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
              指标实时算自申报案:
              <Typography.Text strong style={{ marginLeft: 6 }}>
                {activeCase.policy_pack_name ?? activeCase.policy_pack_id?.slice(0, 8) + '…'}
              </Typography.Text>
              {monitoringQuery.data?.computed_at && (
                <span style={{ marginLeft: 8 }}>
                  · 计算时间 {String(monitoringQuery.data.computed_at).slice(0, 19).replace('T', ' ')}
                </span>
              )}
            </Typography.Text>
            <Tooltip title="重新计算 — 拉案级 monitoring + 就绪报告">
              <Button
                type="primary"
                icon={<IconRefresh size={14} />}
                onClick={handleRefresh}
                loading={monitoringQuery.isFetching}
              >
                重新计算
              </Button>
            </Tooltip>
          </Space>
        </Card>
      )}

      {/* Sprint 35:老 period_id 路径已删 — 真有"历史快照查询"需求未来另起 /history */}

      {/* Sprint 27:case-driven 主体 — 21 项指标实时算结果 */}
      {!noActiveCase && activeCase && monitoringQuery.isLoading && (
        <LoadingSkeleton rows={8} />
      )}
      {!noActiveCase && activeCase && monitoringQuery.isError && (
        <ErrorState
          title="加载案级达标评估失败"
          description={(monitoringQuery.error as Error)?.message}
          onRetry={() => monitoringQuery.refetch()}
        />
      )}
      {!noActiveCase && activeCase && monitoringPayload && (
        <>
          <Card styles={{ body: { padding: 16 } }}>
            <Row gutter={[12, 12]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="规则集"
                  value={monitoringPayload.summary.policy_pack_code ?? '—'}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="目标级别"
                  value={levelLabel(monitoringPayload.summary.target_level)}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="通过 / 总数"
                  // Sprint27-hotfix4:总数 = 全部指标(可算 + 待确认),反映完整指标集
                  value={`${monitoringPayload.summary.passed_count} / ${
                    monitoringPayload.summary.quantitative_total +
                    monitoringPayload.summary.pending_count
                  }`}
                  valueStyle={{
                    fontSize: 16,
                    color:
                      monitoringPayload.summary.failed_count === 0
                        ? 'var(--color-success, #52c41a)'
                        : 'var(--color-error, #cf1322)',
                  }}
                  suffix={
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 11, marginLeft: 4 }}
                    >
                      (数据 {monitoringPayload.summary.passed_count}/
                      {monitoringPayload.summary.quantitative_total} · 人工{' '}
                      {monitoringPayload.summary.pending_count})
                    </Typography.Text>
                  }
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="结论"
                  valueRender={() => {
                    // Sprint27-hotfix4:用 BE 计数生成清晰中文,而非透传 conclusion enum
                    const { passed_count, failed_count, pending_count } =
                      monitoringPayload.summary;
                    const total =
                      monitoringPayload.summary.quantitative_total +
                      pending_count;
                    const parts: string[] = [];
                    parts.push(`${passed_count}/${total} 达标`);
                    if (failed_count > 0) parts.push(`未达 ${failed_count} 项`);
                    if (pending_count > 0)
                      parts.push(`待人工确认 ${pending_count} 项`);
                    const color =
                      failed_count > 0
                        ? 'red'
                        : pending_count > 0
                          ? 'orange'
                          : 'green';
                    return (
                      <Tag
                        color={color}
                        style={{
                          fontSize: 13,
                          whiteSpace: 'normal',
                          lineHeight: '1.5',
                          height: 'auto',
                          padding: '4px 8px',
                          maxWidth: '100%',
                        }}
                      >
                        {parts.join(' · ')}
                      </Tag>
                    );
                  }}
                />
              </Col>
            </Row>
            {/* Sprint 35:数据未就绪改 — 列出具体 pending 指标 + 「去指标绑定」按钮 */}
            {!monitoringPayload.summary.ready_data && (() => {
              const pending = collectByStatus(monitoringPayload.indicators, 'pending');
              // 未达数用 summary.failed_count(主指标层),与顶部"未达 N 项"对齐;
              // collectByStatus 数的是叶子,带子条件的指标会比主层多算 → 同屏不一致
              const failedCount = monitoringPayload.summary.failed_count;
              return (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={`数据未就绪 — 待录 ${pending.length} 项${failedCount ? ` · 未达 ${failedCount} 项` : ''}`}
                  description={
                    <Space direction="vertical" size={6} style={{ width: '100%', marginTop: 4 }}>
                      {pending.slice(0, 10).map((ind) => (
                        <div key={ind.code} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Tag style={{ fontFamily: 'var(--font-family-mono, monospace)', margin: 0 }}>
                            {ind.code}
                          </Tag>
                          <Typography.Text style={{ flex: 1 }}>{ind.name}</Typography.Text>
                          <Button
                            type="link"
                            size="small"
                            onClick={() => navigate(inputRouteForIndicator(ind.code, activeCase!.id))}
                          >
                            去录入 →
                          </Button>
                        </div>
                      ))}
                      {pending.length > 10 && (
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                          还有 {pending.length - 10} 项 — 进案工作台「指标绑定」Tab 一次性处理
                        </Typography.Text>
                      )}
                    </Space>
                  }
                />
              );
            })()}
          </Card>
          <IndicatorList results={monitoringPayload.indicators} />
          <Typography.Text
            type="secondary"
            style={{ fontSize: 'var(--font-size-xs)', display: 'block' }}
          >
            数据来源:申报案 {activeCase.id.slice(0, 8)}… · 引擎按案级指标绑定 + 孵化器档案 + 企业台账实时计算。
            灰色 pending = 数据未录;红色 fail = 不达标;绿色 ok = 达标。
          </Typography.Text>
        </>
      )}
      {/* Sprint 35:删老 period_id 路径 — assessment + readiness + VerdictBox + 250 行死代码
          (SFL 永远不走该路径;真有"历史快照查询"需求未来另起 /history 模块) */}
    </Space>
  );
}
